import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldAlert, BookOpen, Loader2 } from "lucide-react";
import { useAnonymousSession } from "../contexts/AnonymousSessionContext";
import { GeolocationPrompt } from "../components/GeolocationPrompt";
import { getPublicVenue, getOrgLocations, findNearestOrgLocation } from "../features/venue-management/services/venueService";
import type { Venue } from "../features/venue-management/types/venue.types";

type LoadState = 'loading' | 'found' | 'fallback' | 'no_slug';

// Converts a slug like "willowbank-rugby" → "Willowbank Rugby"
const slugToDisplayName = (slug: string): string =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const QREntry = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { setVenueId, setVenueData, setUserGeolocation, userGeolocation } = useAnonymousSession();

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [venue, setVenue] = useState<Venue | null>(null);
  const [locationResolved, setLocationResolved] = useState(false);
  const [locationLabel, setLocationLabel] = useState<string | null>(null);
  const [matchedClubName, setMatchedClubName] = useState<string | null>(null);

  // Resolve venue from database
  useEffect(() => {
    if (!venueId) {
      setLoadState('no_slug');
      return;
    }

    setVenueId(venueId);

    getPublicVenue(venueId).then((result) => {
      console.log('[QREntry] getPublicVenue result:', result);
      if (result) {
        setVenue(result);

        // Org data lives in the location's own metadata (no base_tenants join needed)
        const meta = (result.metadata ?? {}) as Record<string, unknown>;
        console.log('[QREntry] metadata:', meta, 'org_name:', meta.org_name, 'sports:', meta.sports);
        const sportsMapping = (meta.sports ?? {}) as Record<string, string>;
        const clubsEnabled = meta.clubs_enabled === true;
        const orgName = (meta.org_name as string) ?? null;

        setVenueData({
          venue_name: result.venue_name,
          sport: result.sport ?? undefined,
          geolocation: result.geolocation,
          org: orgName ? {
            org_id: result.org_id,
            org_name: orgName,
            sports: sportsMapping,
            clubs_enabled: clubsEnabled,
          } : null,
        });
        setLoadState('found');
      } else {
        setVenueData({ venue_name: slugToDisplayName(venueId) });
        setLoadState('fallback');
      }
    });
  }, [venueId, setVenueId, setVenueData]);

  // Skip geolocation prompt if we already have user location
  useEffect(() => {
    if (userGeolocation) {
      setLocationResolved(true);
    }
  }, [userGeolocation]);

  const handleLocationGranted = (coords: { lat: number; lng: number }) => {
    setUserGeolocation(coords);
    setLocationResolved(true);

    // Geo-match: if this is a default org location, find the nearest club
    const venueMeta = (venue?.metadata ?? {}) as Record<string, unknown>;
    if (venue?.org_id && venueMeta.org_type === 'nso') {
      getOrgLocations(venue.org_id).then((locations) => {
        const nearest = findNearestOrgLocation(locations, coords.lat, coords.lng);
        if (nearest) {
          setMatchedClubName(nearest.venue_name);
          console.log('[QREntry] Geo-matched to nearest club:', nearest.venue_name);
        }
      });
    }

    // Reverse geocode using OpenStreetMap Nominatim (free, no API key)
    fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${coords.lat}&lon=${coords.lng}&format=json`,
      { headers: { 'Accept-Language': 'en' } }
    )
      .then((r) => r.json())
      .then((data) => {
        console.log('[Nominatim]', data);
        const addr = data?.address;
        if (addr) {
          // Cover all Nominatim region variants (India uses city_district, county, etc.)
          const locality =
            addr.suburb ||
            addr.neighbourhood ||
            addr.quarter ||
            addr.village ||
            addr.hamlet ||
            addr.town;
          const city =
            addr.city ||
            addr.city_district ||
            addr.county ||
            addr.municipality;
          const region = addr.state || addr.region;

          const parts = [locality, city, region].filter(Boolean);

          if (parts.length > 0) {
            setLocationLabel(parts.join(', '));
          } else if (data.display_name) {
            // Ultimate fallback — take first 3 comma-separated parts of display_name
            const displayParts = data.display_name.split(',').map((s: string) => s.trim());
            setLocationLabel(displayParts.slice(0, 3).join(', '));
          }
        } else if (data?.display_name) {
          const displayParts = data.display_name.split(',').map((s: string) => s.trim());
          setLocationLabel(displayParts.slice(0, 3).join(', '));
        }
      })
      .catch((err) => {
        console.error('[Nominatim] reverse geocode failed:', err);
        setLocationLabel('Location captured');
      });
  };

  const handleLocationSkipped = () => {
    setLocationResolved(true);
  };

  const handleManualEntry = (_text: string) => {
    // Manual suburb/postcode entered — future: geocode this
    setLocationResolved(true);
  };

  // Loading state
  if (loadState === 'loading') {
    return (
      <div className="min-h-screen bg-sy-navy flex flex-col items-center justify-center px-4">
        <Loader2 className="w-10 h-10 text-sy-teal animate-spin mb-4" />
        <p className="text-white/60 text-sm">Resolving venue...</p>
      </div>
    );
  }

  // No slug in URL at all
  if (loadState === 'no_slug') {
    return (
      <div className="min-h-screen bg-sy-navy flex flex-col items-center justify-center px-4 py-8">
        <img src="/sy-logo-light.png" alt="Charlie" className="h-12 mb-8" />
        <h1 className="text-2xl font-bold text-white mb-3">Invalid QR Code</h1>
        <p className="text-white/60 text-sm text-center mb-6 max-w-sm">
          This QR code link is missing venue information. Please scan the QR code directly from the poster.
        </p>
        <Link
          to="/welcome"
          className="px-6 py-3 rounded-xl bg-sy-teal text-sy-navy font-medium hover:bg-sy-teal/90 transition-colors"
        >
          Go to Charlie Home
        </Link>
      </div>
    );
  }

  // Venue found (from DB) or fallback (slug-only) — show welcome screen either way
  const meta = (venue?.metadata ?? {}) as Record<string, unknown>;
  const orgName = meta.org_type === 'nso' ? (meta.org_name as string) : null;
  const displayName = orgName ?? venue?.venue_name ?? slugToDisplayName(venueId ?? '');
  const cityDisplay = venue?.address?.city;
  const sportsMap = (meta.sports ?? {}) as Record<string, string>;
  const sportLabel = Object.keys(sportsMap).length > 0
    ? Object.values(sportsMap).join(', ')
    : venue?.sport;
  const subtitle = [sportLabel, cityDisplay].filter(Boolean).join(' | ');

  return (
    <div className="min-h-screen bg-sy-navy flex flex-col items-center justify-center px-4 py-8">
      {/* Logo */}
      <img
        src="/sy-logo-light.png"
        alt="Charlie"
        className="h-14 mb-8 animate-fadeInUp"
      />

      {/* Welcome */}
      <div className="text-center mb-6 animate-fadeInUp animation-delay-100">
        <p className="text-white/50 text-sm mb-1">Welcome to</p>
        <h1 className="text-3xl font-bold text-white mb-1">
          {displayName}
        </h1>
        {subtitle && (
          <p className="text-sy-teal/80 text-sm">{subtitle}</p>
        )}
      </div>

      {/* Geolocation Prompt — only if not yet resolved */}
      {!locationResolved && (
        <div className="mb-6 w-full flex justify-center animate-fadeInUp animation-delay-150">
          <GeolocationPrompt
            onLocationGranted={handleLocationGranted}
            onLocationSkipped={handleLocationSkipped}
            onManualEntry={handleManualEntry}
          />
        </div>
      )}

      {/* Location confirmed badge — shown after GPS granted */}
      {locationResolved && userGeolocation && (
        <div className="mb-5 w-full max-w-sm bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeInUp">
          <span className="text-emerald-400 text-lg">📍</span>
          <div>
            <p className="text-emerald-400 text-sm font-medium">
              {locationLabel ?? 'Locating...'}
            </p>
            {matchedClubName && (
              <p className="text-emerald-400/70 text-xs mt-0.5">
                Nearest club: {matchedClubName}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="w-full max-w-sm space-y-4 animate-fadeInUp animation-delay-200 pt-4">
        {/* EMERGENCY — red, 80px tall, pulsing border */}
        <Link
          to="/assessment/public"
          className="flex flex-col items-center justify-center w-full min-h-[80px] rounded-xl bg-sy-red text-white text-lg font-bold px-6 py-4 hover:bg-red-600 transition-all duration-200 border-2 border-sy-red/50"
        >
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-6 h-6" />
            EMERGENCY
          </div>
          <span className="text-sm font-normal text-white/80 mt-1">
            Suspected head injury? Start here
          </span>
        </Link>

        {/* LEARN — teal, 60px tall */}
        {/* <Link
          to="/chat"
          className="flex flex-col items-center justify-center w-full min-h-[60px] rounded-xl bg-sy-teal text-sy-navy text-lg font-bold px-6 py-4 hover:bg-sy-teal/90 transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            LEARN
          </div>
          <span className="text-sm font-normal text-sy-navy/70 mt-1">
            Ask Charlie about concussion
          </span>
        </Link> */}
      </div>

      {/* Medical Disclaimer */}
      <p className="mt-8 text-xs text-white/30 text-center max-w-sm animate-fadeInUp animation-delay-300">
        Charlie is not a medical device. All suspected concussions should be
        assessed by a qualified medical professional. If in doubt, call{" "}
        <a href="tel:000" className="underline text-white/50 hover:text-white/70">
          Triple Zero (000)
        </a>
        .
      </p>
    </div>
  );
};
